import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Trash2, X, Sparkles, Loader2, Star, Eye, AlertTriangle, Bell, BellRing } from "lucide-react";
import { toast } from "sonner";
import api, { apiErrorDetail } from "../lib/api";
import { fmtPrice, fmtNum, fmtPct, changeClass, trendBadgeClass } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Label } from "../components/ui/label";

const AlertDialogBox = ({ open, onOpenChange, ticker, initial, onSave }) => {
  const [form, setForm] = useState({ price_above: "", price_below: "", rsi_above: "", rsi_below: "" });

  useEffect(() => {
    if (open)
      setForm({
        price_above: initial?.price_above ?? "",
        price_below: initial?.price_below ?? "",
        rsi_above: initial?.rsi_above ?? "",
        rsi_below: initial?.rsi_below ?? "",
      });
  }, [open, initial]);

  const save = (clear = false) => {
    const payload = {};
    if (!clear) {
      for (const k of Object.keys(form)) {
        if (form[k] !== "" && !isNaN(Number(form[k]))) payload[k] = Number(form[k]);
      }
    }
    onSave(payload);
  };

  const fields = [
    ["price_above", "Price above"],
    ["price_below", "Price below"],
    ["rsi_above", "RSI above"],
    ["rsi_below", "RSI below"],
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm bg-card border-border sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">Alerts for {ticker}</DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            In-app only — evaluated against cached data on page load and background refresh.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {fields.map(([k, label]) => (
            <div key={k} className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">{label}</Label>
              <Input type="number" step="any" className="rounded-sm bg-background font-mono h-9"
                data-testid={`alert-${k.replace("_", "-")}-input`}
                value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <Button onClick={() => save(false)} className="flex-1 rounded-sm bg-primary hover:bg-primary/85 text-white"
            data-testid="alert-save-button">Save alert</Button>
          <Button onClick={() => save(true)} variant="outline" className="rounded-sm border-border text-muted-foreground hover:text-loss"
            data-testid="alert-clear-button">Clear</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ThesisBlock = ({ thesis, meta }) => (
  <div className="bg-blue-950/20 border border-primary/20 rounded-sm p-4 space-y-3" data-testid={`thesis-content-${meta.ticker}`}>
    <div className="flex items-center justify-between">
      <span className="overline-label text-primary">AI thesis · {meta.ticker}</span>
      <span className="text-[10px] font-mono text-muted-foreground">
        {meta.cached ? "CACHED" : "FRESH"} · {new Date(meta.generated_at).toLocaleString()}
      </span>
    </div>
    <div className="grid md:grid-cols-3 gap-4 text-sm">
      <div>
        <div className="flex items-center gap-1.5 mb-2 text-gain"><Star className="w-3.5 h-3.5" /><span className="overline-label text-gain">On the radar</span></div>
        <ul className="space-y-1.5">{thesis.radar_points.map((p, i) => <li key={i} className="text-gray-200 border-l-2 border-gain/50 pl-2.5">{p}</li>)}</ul>
      </div>
      <div>
        <div className="flex items-center gap-1.5 mb-2 text-loss"><AlertTriangle className="w-3.5 h-3.5" /><span className="overline-label text-loss">Risks</span></div>
        <ul className="space-y-1.5">{thesis.risk_points.map((p, i) => <li key={i} className="text-gray-200 border-l-2 border-loss/50 pl-2.5">{p}</li>)}</ul>
      </div>
      <div>
        <div className="flex items-center gap-1.5 mb-2 text-yellow-500"><Eye className="w-3.5 h-3.5" /><span className="overline-label text-yellow-500">Watch next</span></div>
        <ul className="space-y-1.5">{thesis.watch_next.map((p, i) => <li key={i} className="text-gray-200 border-l-2 border-yellow-500/50 pl-2.5">{p}</li>)}</ul>
      </div>
    </div>
    <p className="text-xs text-muted-foreground pt-2 border-t border-white/10">This is AI-generated research, not financial advice.</p>
  </div>
);

export default function Watchlists() {
  const [lists, setLists] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [name, setName] = useState("");
  const [theses, setTheses] = useState({}); // ticker -> {loading, data}
  const [alertDialog, setAlertDialog] = useState({ open: false, ticker: null, rule: null });

  const saveAlert = async (payload) => {
    try {
      const { data } = await api.put(`/watchlists/${activeId}/alerts/${alertDialog.ticker}`, payload);
      toast.success(data.message);
      setAlertDialog({ open: false, ticker: null, rule: null });
      loadDetail(activeId);
    } catch (e) {
      toast.error(apiErrorDetail(e));
    }
  };

  const loadLists = useCallback(async () => {
    const { data } = await api.get("/watchlists");
    setLists(data.watchlists);
    return data.watchlists;
  }, []);

  const loadDetail = useCallback(async (id) => {
    const { data } = await api.get(`/watchlists/${id}`);
    setDetail(data);
  }, []);

  useEffect(() => {
    loadLists().then((l) => l.length && setActiveId(l[0].id));
  }, [loadLists]);

  useEffect(() => {
    setTheses({});
    if (activeId) loadDetail(activeId);
    else setDetail(null);
  }, [activeId, loadDetail]);

  const createList = async () => {
    if (!name.trim()) return;
    try {
      const { data } = await api.post("/watchlists", { name: name.trim() });
      setDialogOpen(false);
      setName("");
      await loadLists();
      setActiveId(data.id);
    } catch (e) {
      toast.error(apiErrorDetail(e));
    }
  };

  const deleteList = async (id) => {
    await api.delete(`/watchlists/${id}`);
    const l = await loadLists();
    if (activeId === id) setActiveId(l[0]?.id || null);
    toast.success("Watchlist deleted");
  };

  const removeTicker = async (ticker) => {
    await api.delete(`/watchlists/${activeId}/tickers/${ticker}`);
    loadDetail(activeId);
    loadLists();
  };

  const genThesis = async (ticker) => {
    setTheses((t) => ({ ...t, [ticker]: { loading: true } }));
    try {
      const { data } = await api.post(`/watchlists/${activeId}/thesis/${ticker}`);
      setTheses((t) => ({ ...t, [ticker]: { loading: false, data } }));
    } catch (e) {
      toast.error(apiErrorDetail(e));
      setTheses((t) => ({ ...t, [ticker]: undefined }));
    }
  };

  return (
    <div className="space-y-6 fade-up">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline-label mb-2">Your radar</div>
          <h1 className="font-heading font-black tracking-tighter text-4xl sm:text-5xl text-white">Watchlists</h1>
        </div>
        <Button onClick={() => setDialogOpen(true)} data-testid="create-watchlist-button"
          className="rounded-sm bg-primary hover:bg-primary/85 text-white gap-2">
          <Plus className="w-4 h-4" /> New watchlist
        </Button>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="watchlist-tabs">
        {lists.map((w) => (
          <div key={w.id} className={`group flex items-center border rounded-sm transition-colors duration-150 ${
            activeId === w.id ? "border-primary bg-primary/10" : "border-border hover:bg-white/5"
          }`}>
            <button onClick={() => setActiveId(w.id)} data-testid={`watchlist-tab-${w.id}`}
              className={`px-4 py-2 text-sm ${activeId === w.id ? "text-white" : "text-muted-foreground"}`}>
              {w.name} <span className="font-mono text-xs text-muted-foreground ml-1">{w.tickers.length}</span>
            </button>
            <button onClick={() => deleteList(w.id)} data-testid={`delete-watchlist-${w.id}`}
              className="pr-2 text-muted-foreground/40 hover:text-loss transition-colors duration-150">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
        {lists.length === 0 && (
          <p className="text-sm text-muted-foreground" data-testid="no-watchlists-message">
            No watchlists yet — create one, or add stocks from the scanner and stock pages.
          </p>
        )}
      </div>

      {detail && (
        <div className="widget overflow-hidden" data-testid="watchlist-detail">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <span className="overline-label">{detail.name}</span>
            <span className="font-mono text-xs text-muted-foreground">{detail.items.length} holdings on radar</span>
          </div>
          {detail.items.some((i) => i.triggered_alerts?.length > 0) && (
            <div className="px-4 py-3 border-b border-yellow-500/30 bg-yellow-500/5 space-y-1" data-testid="triggered-alerts-banner">
              {detail.items.filter((i) => i.triggered_alerts?.length > 0).map((i) => (
                <div key={i.ticker} className="flex items-center gap-2 text-sm text-yellow-500">
                  <BellRing className="w-3.5 h-3.5 shrink-0" />
                  <span className="font-mono font-medium">{i.ticker}</span>
                  <span className="text-gray-200">{i.triggered_alerts.join(" · ")}</span>
                </div>
              ))}
            </div>
          )}
          {detail.items.length === 0 ? (
            <p className="p-8 text-sm text-muted-foreground">
              Empty. Add tickers from the <Link to="/scanner" className="text-primary hover:underline">scanner</Link> or any stock page.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left">
                    {["Ticker", "Name", "Price", "Day", "PE", "Yield", "RSI", "Trend", ""].map((h, i) => (
                      <th key={i} className={`px-4 py-2.5 overline-label whitespace-nowrap ${i >= 2 && i <= 6 ? "text-right" : ""}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {detail.items.map((s) => {
                    const th = theses[s.ticker];
                    return [
                      <tr key={s.ticker} className="border-b border-border/50 even:bg-white/[0.02]" data-testid={`watchlist-row-${s.ticker}`}>
                        <td className="px-4 py-3 font-mono font-medium whitespace-nowrap">
                          <Link to={`/stock/${s.ticker}`} className="text-primary hover:underline">{s.ticker}</Link>
                        </td>
                        <td className="px-4 py-3 text-gray-300 max-w-[180px] truncate">{s.name}</td>
                        <td className="px-4 py-3 font-mono text-right text-white">{fmtPrice(s.price, s.currency)}</td>
                        <td className={`px-4 py-3 font-mono text-right ${changeClass(s.day_change_pct)}`}>{fmtPct(s.day_change_pct)}</td>
                        <td className="px-4 py-3 font-mono text-right text-gray-300">{s.pe != null ? fmtNum(s.pe, 1) : "—"}</td>
                        <td className="px-4 py-3 font-mono text-right text-gray-300">{s.dividend_yield_pct != null ? `${fmtNum(s.dividend_yield_pct)}%` : "—"}</td>
                        <td className="px-4 py-3 font-mono text-right text-gray-300">{s.rsi14 != null ? fmtNum(s.rsi14, 0) : "—"}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-block px-2 py-0.5 text-[11px] font-mono uppercase border rounded-sm ${trendBadgeClass(s.trend)}`}>{s.trend}</span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right">
                          <button onClick={() => setAlertDialog({ open: true, ticker: s.ticker, rule: s.alert })}
                            data-testid={`alert-button-${s.ticker}`} title="Configure alerts"
                            className={`p-1.5 mr-1 rounded-sm transition-colors duration-150 ${
                              s.triggered_alerts?.length ? "text-yellow-500" : s.alert ? "text-primary" : "text-muted-foreground/50 hover:text-primary"
                            }`}>
                            {s.triggered_alerts?.length ? <BellRing className="w-4 h-4" /> : <Bell className="w-4 h-4" />}
                          </button>
                          <button onClick={() => genThesis(s.ticker)} disabled={th?.loading} data-testid={`thesis-button-${s.ticker}`}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs border border-primary/40 text-primary rounded-sm hover:bg-primary/10 transition-colors duration-150 mr-2">
                            {th?.loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                            AI thesis
                          </button>
                          <button onClick={() => removeTicker(s.ticker)} data-testid={`remove-ticker-${s.ticker}`}
                            className="p-1.5 text-muted-foreground/50 hover:text-loss transition-colors duration-150" title="Remove">
                            <X className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>,
                      th?.data && (
                        <tr key={`${s.ticker}-thesis`}>
                          <td colSpan={9} className="px-4 pb-4 pt-1">
                            <ThesisBlock thesis={th.data.thesis} meta={th.data} />
                          </td>
                        </tr>
                      ),
                    ];
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-sm bg-card border-border sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">New watchlist</DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              Group companies you want to keep on your radar.
            </DialogDescription>
          </DialogHeader>
          <Input autoFocus placeholder="e.g. UK income ideas" className="rounded-sm bg-background"
            data-testid="watchlist-name-input" value={name} onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && createList()} />
          <Button onClick={createList} disabled={!name.trim()} data-testid="watchlist-create-confirm"
            className="rounded-sm bg-primary hover:bg-primary/85 text-white">
            Create
          </Button>
        </DialogContent>
      </Dialog>

      <AlertDialogBox open={alertDialog.open} ticker={alertDialog.ticker} initial={alertDialog.rule}
        onOpenChange={(o) => setAlertDialog({ ...alertDialog, open: o })} onSave={saveAlert} />
    </div>
  );
}
